import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'

import useUser from 'hooks/useUser'
import LoadingStatus from 'state/enums/LoadingStatus'
import { pushPageView } from 'utils/gtm-utils'

import { Page, Title, Titlebar } from './LeadsPage.styled'
import { LeadsPageContext } from './LeadsPageContext'
import LeadQuickViewModal from './partials/LeadQuickViewModal'
import { LeadQuickViewModalRef } from './partials/LeadQuickViewModal/LeadQuickViewModal'
import LeadsFilters from './partials/LeadsFilters'
import LeadsSearch from './partials/LeadsSearch'
import { CtaActions } from './partials/LeadsSearchTable/CtaButtons'
import LeadsSearchTable, {
    LeadsSearchTableRef,
} from './partials/LeadsSearchTable/LeadsSearchTable'
import { ApplicationFilters, LeadResult } from './types'
import { getLeadDetails } from './utils'

export const LEADS_PAGE_ROUTE_PATH = 'leads'

export default function LeadsPage(): JSX.Element {
    const [search, setSearch] = useState('')

    const [leadsSearchResults, setLeadsSearchResults] = useState<LeadResult[]>(
        [],
    )

    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(
        LoadingStatus.IDLE,
    )

    const [applicationFilters, setApplicationFilters] =
        useState<ApplicationFilters>({
            prequalifiedLeads: false,
            declinedLeads: false,
            preApprovedLeads: false,
            dealershipIds: [],
        })

    const setDealershipIds = useCallback((dealershipIds: number[]) => {
        setApplicationFilters((prev) => ({
            ...prev,
            dealershipIds,
        }))
    }, [])

    const leadsPageCtx = useMemo(
        () => ({
            search,
            setSearch,
            applicationFilters,
            setApplicationFilters,
            setDealershipIds,
            loadingStatus,
            setLoadingStatus,
            leadsSearchResults,
            setLeadsSearchResults,
        }),
        [
            search,
            applicationFilters,
            loadingStatus,
            setDealershipIds,
            leadsSearchResults,
        ],
    )

    const leadQuickViewModalRef = useRef<LeadQuickViewModalRef>(null)
    const { user } = useUser()
    const leadsSearchTableRef = useRef<LeadsSearchTableRef>(null)

    const ctaButtonClickedHandler = async (
        action: CtaActions,
        leadId: number,
    ) => {
        let viewedLead = null

        if (action === 'viewLead' && leadQuickViewModalRef?.current) {
            viewedLead = await leadQuickViewModalRef.current.showLead(leadId)
        } else {
            viewedLead = await getLeadDetails(leadId)
        }

        if (leadsSearchTableRef?.current) {
            leadsSearchTableRef.current.markLeadAsViewed(viewedLead)
        }
    }

    useEffect(() => {
        pushPageView(user, 'Leads Page')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <LeadsPageContext.Provider value={leadsPageCtx}>
            <Page>
                <Titlebar>
                    <Title>Consumer Leads</Title>
                    <LeadsSearch />
                    <LeadsFilters />
                </Titlebar>

                <LeadsSearchTable
                    ref={leadsSearchTableRef}
                    cbCtaClicked={ctaButtonClickedHandler}
                />

                <LeadQuickViewModal ref={leadQuickViewModalRef} />
            </Page>
        </LeadsPageContext.Provider>
    )
}
